import React from "react";
import { StyleSheet, View, Animated, Image } from "react-native";
import { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import {
  useFocusEffect,
  useIsFocused,
  RouteProp,
} from "@react-navigation/native";
import { useQuery } from "@apollo/client";
import { colors } from "../../theme";
import Heading from "../../components/Heading";
import useCurrentCustomer from "../../hooks/useCurrentCustomer";
import EmptyCard from "../../components/EmptyCard";
import VisitCard from "../../components/VisitCard";
import { VISITS } from "../queries";
import { VisitFilterStatusType } from "../../types";
import { TabParamList } from "../../navigators/types";
import EnablePushNotifications from "./EnablePushNotifications";
import { Durations } from "../../styles";
import { useFirebaseAuth } from "../../firebase/AuthProvider";

const HeaderHeight = 140;

type ScreenNavigationProp = BottomTabNavigationProp<TabParamList, "Home">;
type ScreenRouteProp = RouteProp<TabParamList, "Home">;

type Props = {
  navigation: ScreenNavigationProp;
  route: ScreenRouteProp;
  dashboardMode?: boolean;
};

const HomeScreen: React.FC<Props> = ({
  navigation,
  route,
  dashboardMode = false,
}) => {
  const { currentCustomer: customer } = useFirebaseAuth();

  const { data, loading, refetch, error } = useQuery(VISITS, {
    variables: {
      filters: { status: VisitFilterStatusType.Upcoming },
    },
    pollInterval: Durations.long,
  });

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const scrollOffsetY = React.useRef(new Animated.Value(0)).current;
  const y = scrollOffsetY.interpolate({
    inputRange: [0, HeaderHeight],
    outputRange: [0, -HeaderHeight],
    extrapolateRight: "clamp",
  });

  return (
    <View style={styles.container}>
      <EnablePushNotifications maybeOpen={!!route.params?.visitConfirmed} />

      {!dashboardMode && (
        <Animated.View
          style={[styles.header, { transform: [{ translateY: y }] }]}
        >
          <Image
            source={require("../../../assets/illustrations/home.png")}
            width={218}
            height={140}
          />
        </Animated.View>
      )}
      <Animated.FlatList
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollOffsetY } } }],
          { useNativeDriver: true },
        )}
        contentContainerStyle={[
          styles.listContainer,
          dashboardMode && styles.dashboardListContainer,
        ]}
        data={data?.visits?.edges || []}
        keyExtractor={(e) => e.node.id}
        refreshing={loading}
        renderItem={({ item, index }) => (
          <VisitCard key={item.key} visit={item.node} featured={index === 0} />
        )}
        ListHeaderComponent={
          dashboardMode
            ? null
            : () => (
                <View style={styles.welcomeHeader}>
                  <Heading size="xl" style={styles.welcomeHeader__Text}>
                    Welcome back
                    {customer?.firstName && `, ${customer?.firstName}`}!
                  </Heading>
                </View>
              )
        }
        ListEmptyComponent={
          loading ? null : (
            <EmptyCard
              title="No upcoming visits"
              description="Ready for your next appointment? Use New Visit above to get started."
            />
          )
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
  },
  header: {
    backgroundColor: colors.yellow,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingTop: HeaderHeight,
  },
  dashboardListContainer: {
    alignSelf: "center",
    maxWidth: 1080,
    paddingBottom: 32,
    paddingTop: 20,
    width: "100%",
  },
  welcomeHeader: {
    backgroundColor: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.darkestGrey,
    paddingVertical: 20,
    marginBottom: 20,
  },
  welcomeHeader__Text: {
    color: colors.white,
  },
});

export default HomeScreen;
